import { NFTI, TransactionMethod } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import {
    StyledCloseIcon,
    StyledDots,
} from 'src/features/common/collectible/components/transaction-status-notification/styles';
import { IRegistryNotification } from 'src/services/notifications/types';
import transactionsRegistry from 'src/services/transactions/transactions-registry';
import { IRegistryTransaction, ITransactionStatusE } from 'src/services/transactions/types';
import { Avatar } from 'src/ui/common/atoms/avatar';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { Typography } from 'src/ui/common/atoms/typography';
import TooltipComponent from 'src/ui/common/organisms/tooltip/tooltip.component';

const METHOD_STATUS_MAPPING: Record<TransactionMethod, string> = {
    mint: 'Minting',
    createAndSell: 'Minting and listing',
    mintAndBuy: 'Minting and buying',
    sell: 'Selling',
    burnLot: 'Burning',
    burnNFT: 'Burning',
    buy: 'Buying',
    placeBid: 'Placing bid',
    transferOwnership: 'Transferring',
    pause: 'Unlisting from sale',
    finalize: 'Finalizing',
    simpleTransfer: 'Simple transfer',
    genericContractTransaction: 'Generic Contract Transaction',
};

const STATUS_MAPPING = {
    [ITransactionStatusE.Accepted]: 'Accepted',
    [ITransactionStatusE.Pending]: 'Pending',
    [ITransactionStatusE.Rejected]: 'Rejected',
    [ITransactionStatusE.RejectedByTTL]: 'There is no response for the transaction, please reload the page',
    [ITransactionStatusE.Success]: 'Transaction is complete',
};

const TransactionStatusNotification = ({
    notification,
    nft,
    successCb,
}: {
    notification: IRegistryNotification;
    nft: NFTI;
    successCb?: () => void;
}) => {
    const { t } = useTranslation(['actions']);
    const [transaction, setTransaction] = useState<IRegistryTransaction | undefined>();
    const rootRef = useRef(null);
    const trIdRef = useRef(transaction?.id);
    const intervalRef = useRef<any>();
    const [dots, setDots] = useState('');

    const close = useCallback(() => {
        if (trIdRef.current) {
            setTransaction(undefined);
            transaction?.remove();
            trIdRef.current = undefined;
            setTimeout(notification.remove, 0);
        }
    }, [notification.remove, transaction]);

    const handleTransactions = useCallback(
        (transactions: IRegistryTransaction[]) => {
            const lastTransaction = transactions.filter((tr) => tr.token.id === nft?.id).pop();

            if (!lastTransaction) {
                close();
            } else {
                setTransaction(lastTransaction);
                trIdRef.current = lastTransaction.id;
            }
            if (lastTransaction?.status === ITransactionStatusE.Success && successCb) {
                successCb();
            }
        },
        [close, nft?.id, successCb]
    );

    useEffect(() => {
        handleTransactions(transactionsRegistry.getAll());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            if (!rootRef.current) {
                clearInterval(intervalRef.current);
                return;
            }
            setDots((prev) => (prev.length > 3 ? '' : `${prev}.`));
        }, 200);

        return () => {
            clearInterval(intervalRef.current);
        };
    }, []);

    useEffect(() => {
        if (nft?.id) {
            transactionsRegistry.subscribe(handleTransactions);
        }
        return () => {
            transactionsRegistry.unsubscribe(handleTransactions);
        };
    }, [nft?.id, handleTransactions]);

    if (!transaction) {
        return null;
    }

    const statusType = METHOD_STATUS_MAPPING[transaction.trMethodName];

    if (transaction.status === ITransactionStatusE.Success) {
        const nftUri = nft.previewAssets?.webps?.smallUrl ?? nft.previewAssets?.originalUrl;

        return (
            <FlexBox ref={rootRef}>
                <Avatar size={70} variant="square" src={nftUri} />
                <Spacing ml="15px">
                    <Typography medium size="sm" color="white" tag="div">
                        Transaction is complete!
                    </Typography>
                    <Spacing mt="10px">
                        <Link href={generatePath(RouteE.COLLECTIBLE_DETAILS, { id: nft?.id })}>
                            <Button onClick={close} variant="outline">
                                Open NFT page
                            </Button>
                        </Link>
                    </Spacing>
                </Spacing>
                <StyledCloseIcon onClick={close} variant="regular" icon="cross" />
            </FlexBox>
        );
    }

    return (
        <FlexBox alignItems="center" ref={rootRef}>
            <Typography semiBold color="white">
                {statusType}. {STATUS_MAPPING[transaction.status]}
            </Typography>
            {transaction.status === ITransactionStatusE.Pending && (
                <>
                    <StyledDots>{dots}</StyledDots>
                    <TooltipComponent
                        content={
                            <Typography size="xs" color="warmBlack">
                                {t('walletActions.pendingTime')}
                                <Spacing ml="6px">
                                    <Typography size="xs" color="warmBlack" bold>
                                        3min
                                    </Typography>
                                </Spacing>
                            </Typography>
                        }
                    >
                        <SvgIcon variant="bold" icon="information" />
                    </TooltipComponent>
                </>
            )}
            {transaction.status !== ITransactionStatusE.Pending && (
                <StyledCloseIcon onClick={close} variant="regular" icon="cross" />
            )}
        </FlexBox>
    );
};

export default TransactionStatusNotification;